/*!
 * 修改密碼 Modal（v2.32.0）
 *
 * 從右上角使用者選單開啟，**任何登入者都能用**（不需要任何 cap）。
 *
 * 在這之前，一般使用者首次登入被強制改過密碼之後，就再也沒有任何地方
 * 可以自己換密碼了 —— 只能請管理者重設。那不合理：密碼外流、或單純想換，
 * 都不該需要驚動別人。
 *
 * 規則與首次登入那頁共用 `utils/passwordPolicy.js`，避免兩份實作漂掉。
 */
import React, { useState } from "react";
import {
  Alert, Button, Form, FormGroup, Input, Label,
  Modal, ModalBody, ModalFooter, ModalHeader,
} from "reactstrap";
import axios from "axios";

import {
  COLORS, cancelBtnStyle, inputStyle, labelStyle,
  modalBodyStyle, modalFooterStyle, modalHeaderStyle, saveBtnStyle,
} from "constant/adminTheme";
import { MIN_LENGTH, validateNewPassword } from "utils/passwordPolicy";

export default function ChangePasswordModal({ isOpen, toggle }) {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirm("");
    setError(null);
    setDone(false);
    setSaving(false);
  };

  const close = () => {
    reset();
    toggle();
  };

  const submit = async (e) => {
    e.preventDefault();
    const problem = validateNewPassword(oldPassword, newPassword, confirm);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await axios.post("/api/change-password", {
        old_password: oldPassword,
        new_password: newPassword,
      });
      if (res.data.code === 1) {
        setDone(true);
      } else {
        setError(res.data.msg || "修改失敗");
      }
    } catch (err) {
      setError(err.response?.data?.msg || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={close}>
      <ModalHeader style={modalHeaderStyle} toggle={close}>
        <i className="fa fa-key mr-2" style={{ color: COLORS.warning }} />修改密碼
      </ModalHeader>
      <ModalBody style={modalBodyStyle}>
        {done ? (
          <Alert color="success" className="mb-0">
            <i className="fa fa-check-circle mr-2" />
            密碼已更新。下次登入請用新密碼。
          </Alert>
        ) : (
          <Form onSubmit={submit}>
            {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
            <FormGroup>
              <Label style={labelStyle}>目前的密碼</Label>
              <Input type="password" style={inputStyle} value={oldPassword}
                autoComplete="current-password"
                onChange={(e) => setOldPassword(e.target.value)} />
            </FormGroup>
            <FormGroup>
              <Label style={labelStyle}>新密碼</Label>
              <Input type="password" style={inputStyle} value={newPassword}
                autoComplete="new-password"
                onChange={(e) => setNewPassword(e.target.value)} />
              <small style={{ color: COLORS.textMuted }}>至少 {MIN_LENGTH} 個字元</small>
            </FormGroup>
            <FormGroup className="mb-0">
              <Label style={labelStyle}>再輸入一次</Label>
              <Input type="password" style={inputStyle} value={confirm}
                autoComplete="new-password"
                onChange={(e) => setConfirm(e.target.value)} />
            </FormGroup>
            {/* 讓 Enter 能送出（button 在 footer，不在 form 裡）*/}
            <button type="submit" style={{ display: "none" }} aria-hidden="true" />
          </Form>
        )}
      </ModalBody>
      <ModalFooter style={modalFooterStyle}>
        {done ? (
          <Button style={saveBtnStyle(false)} onClick={close}>關閉</Button>
        ) : (
          <>
            <Button style={cancelBtnStyle} onClick={close}>取消</Button>
            <Button style={saveBtnStyle(saving)} disabled={saving} onClick={submit}>
              {saving ? "更新中..." : "更新密碼"}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}
